import { RefObject, useEffect, useState } from 'react'

import {
  getColumnSelector,
  LINKED_ATTRIBUTES,
  LIST_SELECTOR,
  ROW_SELECTOR,
  SELECTED_ROW_SELECTOR,
} from './linked-columns.attributes'
import { Point } from './linked-columns.utils'

const SETTLE_MS = 500

export interface LinkMeasure {
  children: Point[]
  parent: Point
  parentKey: string
  signature: string
  spineX: number
  toPanelLeft: number
}

const round = (value: number) => Math.round(value * 10) / 10

type VerticalRange = Pick<DOMRect, 'bottom' | 'top'>

const isCenterVisible = (rect: DOMRect, list: VerticalRange) => {
  const center = rect.top + rect.height / 2
  return center >= list.top && center <= list.bottom
}

// Note: the list padding is no row's place - the pinned row sits at its edge, so a row whose center
// is in the padding is scrolling out behind it and would blink a branch in and out
const getContentRange = (list: Element): VerticalRange => {
  const rect = list.getBoundingClientRect()
  const { paddingBottom, paddingTop } = getComputedStyle(list)

  return {
    bottom: rect.bottom - parseFloat(paddingBottom),
    top: rect.top + parseFloat(paddingTop),
  }
}

// Note: the selected row is sticky, so a row that scrolls under it is hidden and must not get a branch
const isCenterCovered = (row: Element, cover: DOMRect) => {
  const rect = row.getBoundingClientRect()
  const center = rect.top + rect.height / 2
  return center >= cover.top && center <= cover.bottom
}

const measure = (container: HTMLElement, from: string, to: string): LinkMeasure | null => {
  const fromColumn = container.querySelector(getColumnSelector(from))
  const toColumn = container.querySelector(getColumnSelector(to))
  const parentRow = fromColumn?.querySelector(SELECTED_ROW_SELECTOR)
  const fromList = fromColumn?.querySelector(LIST_SELECTOR)
  const toList = toColumn?.querySelector(LIST_SELECTOR)
  const toPanel = toList?.parentElement

  if (!fromColumn || !toColumn || !parentRow || !fromList || !toList || !toPanel) return null

  const origin = container.getBoundingClientRect()
  const parentRect = parentRow.getBoundingClientRect()
  if (!isCenterVisible(parentRect, fromList.getBoundingClientRect())) return null

  const toListRange = getContentRange(toList)
  const pinnedRow = toColumn.querySelector(SELECTED_ROW_SELECTOR)
  const pinnedRect = pinnedRow?.getBoundingClientRect()
  const children = [...toColumn.querySelectorAll(ROW_SELECTOR)]
    .filter((row) => row === pinnedRow || !pinnedRect || !isCenterCovered(row, pinnedRect))
    .map((row) => row.getBoundingClientRect())
    .filter((rect) => isCenterVisible(rect, toListRange))
    .map((rect) => ({
      x: round(rect.left - origin.left),
      y: round(rect.top + rect.height / 2 - origin.top),
    }))

  if (children.length === 0) return null

  const toPanelRect = toPanel.getBoundingClientRect()
  const parent = {
    x: round(parentRect.right - origin.left),
    y: round(parentRect.top + parentRect.height / 2 - origin.top),
  }
  const spineX = round(
    (fromColumn.getBoundingClientRect().right + toColumn.getBoundingClientRect().left) / 2 -
      origin.left,
  )
  const measured = {
    children,
    parent,
    parentKey: parentRow.getAttribute(LINKED_ATTRIBUTES.row) ?? '',
    spineX,
    toPanelLeft: round(toPanelRect.left - origin.left),
  }

  return { ...measured, signature: JSON.stringify(measured) }
}

export const useLinkMeasure = (
  containerRef: RefObject<HTMLElement | null>,
  from: string,
  to: string,
) => {
  const [state, setState] = useState<LinkMeasure | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    let frame = 0
    let settleUntil = 0

    const update = () => {
      const next = measure(container, from, to)
      setState((prev) => (prev?.signature === next?.signature ? prev : next))
    }

    const tick = () => {
      update()
      if (performance.now() < settleUntil) frame = requestAnimationFrame(tick)
    }

    // Note: rows move while their entrance animation plays, so a change is followed for a moment
    const settle = () => {
      settleUntil = performance.now() + SETTLE_MS
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(tick)
    }

    const resizeObserver = new ResizeObserver(settle)
    resizeObserver.observe(container)

    const mutationObserver = new MutationObserver(settle)
    mutationObserver.observe(container, {
      attributeFilter: [LINKED_ATTRIBUTES.selected],
      attributes: true,
      childList: true,
      subtree: true,
    })

    container.addEventListener('scroll', update, true)
    container.addEventListener('animationend', update)
    window.addEventListener('resize', settle)
    settle()

    return () => {
      cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      container.removeEventListener('scroll', update, true)
      container.removeEventListener('animationend', update)
      window.removeEventListener('resize', settle)
    }
  }, [containerRef, from, to])

  return state
}
